/**
 * The handful of class strings every primitive shares.
 *
 * Not a styling system — Tailwind is that. These are the three decisions that
 * must be identical across every control, where "identical" is the point and a
 * near-miss is the failure: a focus ring that is 2px on one button and 3px on
 * the next reads as a rendering bug, and a hit area that is 48px on most
 * controls and 34px on one is a control nobody can press on a phone.
 */

/**
 * Joins class names, dropping anything falsy.
 *
 * A local four-line helper rather than `clsx`. Every primitive here composes a
 * base string with two or three conditional ones, and a dependency for that is
 * a dependency to keep current forever.
 */
export function cx(...parts: (string | false | null | undefined)[]): string {
	return parts.filter(Boolean).join(" ");
}

/**
 * The focus ring: a 3px `--brand-subtle` halo and a `--ring` border.
 *
 * `focus-visible` rather than `focus`, which is what keeps it off a button
 * somebody clicked with a mouse while keeping it on for everybody navigating by
 * keyboard — the people it exists for.
 *
 * `outline-none` on its own would be an accessibility failure, so it is never
 * written without the ring that replaces it. Keeping the pair in one constant
 * is what makes that true by construction rather than by review.
 */
export const FOCUS_RING =
	"outline-none focus-visible:ring-3 focus-visible:ring-ring-halo " +
	"focus-visible:border-ring focus-visible:z-10";

/**
 * A 48px minimum height below 640px.
 *
 * The design draws a 34px button, which is right for a mouse on a 1440px
 * screen and too small for a thumb. `10/06` §8 and every mobile guideline put
 * the floor at around 48px, so the controls grow on a phone and stay dense on a
 * desktop. `min-h-12` is 48px because `--spacing` is 4px.
 *
 * It is a minimum rather than a fixed height, so a control that is already
 * taller — a textarea, a large button — is untouched.
 */
export const TOUCH_TARGET = "max-sm:min-h-12";

/** What a disabled control looks like, everywhere. */
export const DISABLED =
	"disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-fg-subtle " +
	"disabled:border-border";

/**
 * The six tones a status can carry, plus `brand`.
 *
 * Five of them are the `-bg` / `-fg` / `-border` triads in `index.css`, and
 * `brand` is the exception: `--brand` is a fill and `--brand-ink` is text,
 * because in dark mode a fill dark enough to carry white text is unreadable
 * *as* text. So the brand chip is solid navy with `--fg-on-brand` on it, and
 * `scripts/check-tokens.mjs` fails the build if anybody invents the missing
 * triad.
 */
export type Tone = "neutral" | "info" | "brand" | "success" | "warning" | "danger";

/** The classes for one tone's filled-pill treatment. */
export const TONE_CLASSES: Record<Tone, string> = {
	neutral: "bg-neutral-bg text-neutral-fg border-neutral-border",
	info: "bg-info-bg text-info-fg border-info-border",
	brand: "bg-brand text-fg-on-brand border-brand",
	success: "bg-success-bg text-success-fg border-success-border",
	warning: "bg-warning-bg text-warning-fg border-warning-border",
	danger: "bg-danger-bg text-danger-fg border-danger-border",
};
